import BigNumber from 'bignumber.js'
import { RouteConfig } from 'config/constants/route'
import { Token } from 'config/types'
import Column from 'layout/Components/Column'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import QRCode from 'qrcode.react'
import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useListTokenActivePayment, useListTokens } from 'state/app/hooks'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import { ModalColorTheme } from 'theme/modal/colors'
import AlchemyModal from 'UIKit/AlchemyModal'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { FlexProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button'
import CopyButton from 'UIKit/Button/CopyButton'
import IconButton from 'UIKit/Button/IconButton'
import FormControl from 'UIKit/FormControl'
import FormInput, { FormInputProps } from 'UIKit/FormControl/FormInput'
import Image from 'UIKit/Image'
import InputAdornment from 'UIKit/Input/InputAdornment'
import { InputContainer, InputLabel, InputMessage } from 'UIKit/Input/styled'
import Link from 'UIKit/Link'
import Search from 'UIKit/Search'
import Skeleton from 'UIKit/Skeleton'
import Text, { TextProps } from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import TokenInput, { TokenInputProps } from 'UIKit/TokenInput'
import TokenLogo from 'UIKit/TokenLogo'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { getNetworkInfo, isFiatNetwork } from 'utils/network'
import GameInfoMessage from 'views/GameDetails/GameInfoMessage'
import { StyledSelectTrigger } from 'views/Payment/DesktopPayment/components/PaymentSelect'
import { PaymentPageEnums } from 'views/Payment/Menu'
import { extractPaymentTokenKey, formatPaymentTokenKey } from '../hooks/useTokens'
import { IStoredSearchToken } from './PaymentTokenSelectModal/useStoredSearchToken'

export const PAYMENT_MODAL_THEME = 'darkblue'

export const StylePaymentModal = styled(AlchemyModal).attrs<{
  colorTheme: ModalColorTheme
}>({
  colorTheme: PAYMENT_MODAL_THEME,
})`
  --pm-bg: ${({ theme, colorTheme }) => theme.modal.colors[colorTheme].background};

  --pm-card: ${({ theme }) => theme.colors.card_primary};
  --pm-card-active: ${({ theme }) => theme.colors.card_primary_active};
  --pm-card-active-hover: ${({ theme }) => theme.colors.card_primary_active_alt};
  --pm-dropdown-color: ${({ theme }) => theme.colors.bg_surface};
  --pm-card-disabled: ${({ theme }) => theme.colors.card_subtle_overlay};

  --pm-input-height: 44px;
  --pm-input-radius: 12px;

  --pm-trigger-height: 44px;
  --pm-trigger-radius: 12px;
  --pm-trigger-radius-small: 4px;

  --pm-color: ${({ theme }) => theme.colors.text_primary};

  --pm-text-trigger-color: ${({ theme }) => theme.colors.text_primary};
  --pm-icon-color: ${({ theme }) => theme.colors.text_secondary};

  --pm-label-color: ${({ theme }) => theme.colors.text_secondary};
  --pm-label-hover-color: ${({ theme }) => theme.colors.text_secondary_hover};
  --pm-label-size: 14px;

  --pm-text-note-size: 12px;
  --pm-text-note-color: ${({ theme }) => theme.colors.text_tertiary};

  --pm-text-disabled: ${({ theme }) => theme.colors.text_secondary};

  --pm-space-label: ${({ theme }) => theme.spacing.spacing_label_field};
  --pm-space-component: 20px;

  --pm-border: ${({ theme }) => theme.colors.stroke_field};
  --pm-border-active: ${({ theme }) => theme.colors.stroke_field_active};
  --pm-stroke: ${({ theme }) => theme.colors.stroke_divider};

  // history
  --pm-history-title: ${({ theme }) => theme.colors.text_primary};
  --pm-history-amount: ${({ theme }) => theme.colors.text_secondary};
  --pm-history-note: ${({ theme }) => theme.colors.text_tertiary};
`

export const PaymentHeading = styled(Text)`
  letter-spacing: -0.32px;
`

PaymentHeading.defaultProps = {
  fontSize: '16px',
  fontWeight: 600,
}

export const PaymentLabel = styled(Text)`
  letter-spacing: -0.28px;
  font-size: 14px;
  font-weight: 500;
`
PaymentLabel.defaultProps = {
  color: 'var(--pm-label-color)',
}

export const PaymentFormControl = styled(FormControl)<{ disabled?: boolean }>`
  ${({ disabled }) =>
    disabled
      ? css`
          &:hover .wrap-input-container,
          .wrap-input-container:hover {
            border-color: transparent;
          }
        `
      : css`
          &:hover {
            .wrap-input-container {
              border-color: transparent;
            }
          }
          &:focus-within {
            .wrap-input-container {
              border-color: var(--pm-border-active);
              transition: color 0.2s;

              &:has(input:disabled) {
                border-color: var(--pm-border-active);
              }
            }
          }
        `}

  .wrap-input-container {
    background: var(--pm-card) !important;
    border-color: transparent;

    height: var(--pm-input-height);
    border-width: 2px;
    border-style: solid;
    border-color: transparent;
    border-radius: var(--pm-trigger-radius);

    #token-input {
      background: transparent !important;
    }
  }

  ${StyledSelectTrigger} {
    .wrap-input-container {
      height: var(--pm-input-height);
    }
  }

  ${InputLabel} {
    font-size: var(--pm-label-size);
    color: var(--pm-label-color);
    margin-bottom: 0;
  }

  ${InputMessage} {
    color: ${theme.colors.error} !important;
    margin-top: 0;
  }
`

export const PaymentFormInput = styled(FormInput)``
export const PaymentFormInputAmount: React.FC<
  Omit<FormInputProps, 'max'> & {
    isInteger?: boolean
  } & TokenInputProps
> = ({ onValueChanged, control, icon, isInteger, ...props }) => {
  return (
    <InputContainer className="wrap-input-container">
      <InputAdornment>
        {icon && (
          <Box width="20px" mr="6px">
            {icon}
          </Box>
        )}
        <PaymentTokenInput
          {...control}
          onValueChanged={(e) => {
            const onChange = onValueChanged || control.onValueChanged
            if (isInteger) {
              if ((e === '' || /^[0-9]*$/.test(e)) && onChange) {
                onChange(e)
              }
            } else if (onChange) onChange(e)
          }}
          {...props}
        />
      </InputAdornment>
    </InputContainer>
  )
}

export const PaymentTokenInput = styled(TokenInput)`
  height: var(--pm-input-height, 44px);
  border: 0;
  padding-inline: 12px;
  width: 100%;
  font-size: 18px;

  #token-input-inner {
    padding: 0;

    .token-name {
      font-size: 14px;
      margin-right: 0;
      color: white;
      font-weight: 500;
    }
  }

  input {
    text-align: left;
    font-size: 1em;
  }

  .max-button {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const PaymentInputContainer = styled(Flex)<{ disabled?: boolean }>`
  overflow: hidden;
  border-radius: var(--pm-trigger-radius);
  color: ${({ color }) => color};

  ${({ disabled }) =>
    disabled
      ? css`
          background: var(--pm-card-disabled);
          cursor: not-allowed;
          color: var(--pm-text-disabled);

          & > * {
            color: var(--pm-text-disabled);
          }
        `
      : css`
          :active {
            background: var(--pm-card-active);
          }
        `};
`

PaymentInputContainer.defaultProps = {
  height: 'var(--pm-trigger-height)',
  padding: '8px 12px',
  background: `var(--pm-card, ${theme.colors.backgroundAlt14})`,
  gap: '8px',
  color: 'white',
  alignItems: 'center',
}

export const PaymentCurrencyBlockMessage: React.FC<{
  currency: Token
  page: PaymentPageEnums
  reason?: 'locked' | 'suspended'
}> = ({ currency, page, reason = 'suspended' }) => {
  const { t } = useTranslation()
  const network = getNetworkInfo(currency.network)?.network

  return currency && network ? (
    <GameInfoMessage
      variant="error"
      maxWidth="100% !important"
      message={
        page === PaymentPageEnums.Deposit
          ? reason === 'suspended'
            ? t('Deposit with {{tokenName}}({{network}}) has been suspended.', {
                tokenName: currency.name,
                network: network.networkInfo.displayName,
              })
            : ''
          : reason === 'suspended'
          ? t('Withdraw with {{tokenName}}({{network}}) has been suspended.', {
              tokenName: currency.name,
              network: network.networkInfo.displayName,
            })
          : reason === 'locked'
          ? t(
              t('{{tokenName}}({{network}}) withdraw suspended for development. Please contact CS for assistance.', {
                tokenName: currency.name,
                network: network.networkInfo.displayName,
              }),
            )
          : ''
      }
    />
  ) : (
    <></>
  )
}

export const AmountDisplay: React.FC<
  {
    title: string
  } & (
    | {
        amount?: BigNumber
        currency?: Token
      }
    | {
        amount: BigNumber
        currency: Token
      }
  ) &
    TextProps
> = ({ title, amount, currency, ...props }) => {
  return (
    <Text
      fontSize="12px"
      fontWeight={400}
      style={{ gap: '4px' }}
      display="flex"
      color="var(--pm-text-note-color)"
      {...props}
    >
      <Trans>{title}</Trans>
      {(amount || currency) && (
        <>
          :
          <span>
            {amount && currency ? (
              <>
                {getFullDisplayBalance(amount, 0, 6)} {currency.name}
              </>
            ) : (
              <Skeleton minWidth="50px" minHeight="16px !important" />
            )}
          </span>
        </>
      )}
    </Text>
  )
}

export const StickyButtonWrapper: React.FC<FlexProps> = ({ ...props }) => {
  return (
    <Flex
      justifyContent="flex-end"
      flexDirection="column"
      width="100%"
      position="relative"
      mt={['auto', '', '', '20px']}
      {...props}
    />
  )
}

export const StyledActionButton = styled(Button).attrs((props) => ({
  height: props.height || ['48px', '', '', '40px'],
  minHeight: props.minHeight || ['48px', '', '', '40px'],
}))`
  border-radius: var(--pm-trigger-radius-small);
  font-size: 14px;
  font-weight: 600;

  ${Text} {
    font-size: 14px;
    font-weight: 600;
  }
`

export const PaymentAppSearch = styled(Search)`
  border-radius: var(--pm-trigger-radius);

  border: 0 !important;
  background: var(--pm-card-active);
  color: ${({ theme }) => theme.colors.textAlt5};
  width: 100%;
  transition: color 200ms;
  min-height: var(--pm-input-height, 44px) !important;
  height: var(--pm-input-height, 44px) !important;

  svg path {
    fill: currentColor !important;
  }
`

export const PaymentSuggestCurrencyHistory: React.FC<
  {
    control: IStoredSearchToken
    onSelectKey: (str: string) => void
    isIncludeFiat?: boolean
    hideNetwork?: boolean
  } & FlexProps
> = ({ control, onSelectKey, isIncludeFiat, hideNetwork = true, ...props }) => {
  const listToken = useListTokens()
  const keyByListToken = useMemo(() => {
    return listToken.reduce((state, token) => {
      state[formatPaymentTokenKey(token.code, token.address, token.network)] = token
      return state
    }, [])
  }, [listToken])
  const filterOption = useMemo(
    () =>
      (control?.tokens || []).filter(
        (str) =>
          str && (isIncludeFiat ? true : !isFiatNetwork(extractPaymentTokenKey(str)?.network)) && keyByListToken[str],
      ),
    [control?.tokens, keyByListToken],
  )

  return (
    filterOption.length > 0 && (
      <Column gap="var(--pm-space-label, 8px)" {...props}>
        <RowBetween>
          <PaymentLabel bold>
            <Trans>History</Trans>
          </PaymentLabel>
          <IconButton p="0" onClick={control.clear}>
            <Icons.TrashBinV2Icon />
          </IconButton>
        </RowBetween>
        <Flex width="100%" overflowX="hidden">
          <RowMiddle overflowX="auto" width="100%" gap="var(--pm-space-label, 8px)" pb="8px">
            {filterOption.map((str) => {
              const token = keyByListToken[str]
              return (
                token && (
                  <StyledIconButton onClick={() => onSelectKey(str)}>
                    <TokenLogo hideNetwork={hideNetwork} token={token} size={20} />
                    {token.name}
                  </StyledIconButton>
                )
              )
            })}
          </RowMiddle>
        </Flex>
      </Column>
    )
  )
}

const StyledIconButton = styled(IconButton)`
  border-radius: var(--pm-trigger-radius-small);
  background: var(--pm-card-active);
  padding: 0 6px;
  height: 30px;

  color: var(--pm-label-color);

  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--pm-card-active-hover);
  }
`

export const PaymentCopyButton: React.FC<{
  text: string
  disabled?: boolean
  hideIcon?: boolean
}> = ({ text, disabled, hideIcon }) => (
  <StyledCopyButton
    ml="auto"
    disabled={disabled}
    $disabled={disabled}
    iconSize={18}
    icon={
      <>
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'currentcolor',
            lineHeight: 1.5,
          }}
        >
          <Trans>Copy</Trans>
        </span>
        {!hideIcon && <StyledCopyIcon width={14} height={16} />}
      </>
    }
    text={text}
  />
)

const StyledCopyButton = styled(CopyButton)<{ $disabled: boolean }>`
  background: var(--pm-card-active, rgba(255, 255, 255, 0.08));
  color: var(--pm-icon-color, white);
  min-width: 76px;
  height: 28px;
  border-radius: 48px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  font-size: 12px;
  gap: 8px;

  &:disabled {
    cursor: auto;
    opacity: 0.8;
  }

  ${({ $disabled }) =>
    $disabled
      ? css`
          background: var(--pm-card-disabled);
          cursor: not-allowed;
          color: var(--pm-text-disabled);

          & > * {
            color: var(--pm-text-disabled);
          }
        `
      : css`
          &:hover {
            background: var(--pm-card-active-hover, rgba(255, 255, 255, 0.1));
            color: white;
          }
        `}
`

export const StyleSmallAction = styled(Button)<{ $disabled?: boolean; active?: boolean }>`
  background: var(--pm-card-active, rgba(255, 255, 255, 0.08));
  color: var(--pm-icon-color, white);
  min-width: 76px;
  height: 28px;
  border-radius: 48px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  font-size: 12px;
  gap: 8px;
  border-width: 1px;
  border-style: solid;
  border-color: transparent;

  &:disabled {
    cursor: auto;
    opacity: 0.8;
  }

  ${({ active }) =>
    active &&
    css`
      background: var(--pm-card-active-hover, rgba(255, 255, 255, 0.08));
      color: white;
      border-color: ${({ theme }) => theme.colors.primary};
    `}

  ${({ $disabled }) =>
    $disabled
      ? css`
          background: var(--pm-card-disabled);
          cursor: not-allowed;
          color: var(--pm-text-disabled);

          & > * {
            color: var(--pm-text-disabled);
          }
        `
      : css`
          &:hover {
            background: var(--pm-card-active-hover, rgba(255, 255, 255, 0.1));
            color: white;
          }
        `}
`

const StyledCopyIcon = styled(Icons.CopyIcon)`
  min-width: 16px;
  margin-left: 6px;

  path {
    fill: currentColor;
  }
`

export const LockBonusAmountWithdrawOverview: React.FC<{
  lockAmount: BigNumber
  selectedToken: Token
  onClose: () => void
}> = ({ lockAmount, selectedToken, onClose }) => {
  return (
    <RowBetween
      style={{
        fontSize: 'var(--pm-text-note-size,12px)',
      }}
      gap="4px"
    >
      <Text fontSize="1em" color="var(--pm-text-note-color)">
        <Trans
          i18nKey="lockBonusAmountWithdrawTitle"
          components={{
            highlight_tag: (
              <Text
                fontSize="1em"
                color="strokeAlt3"
                fontWeight={500}
                style={{
                  textDecoration: 'underline',
                  display: 'inline',
                }}
                as={Link}
                onClick={onClose}
                href={RouteConfig.BonusCenter}
              />
            ),
          }}
        />
      </Text>
      <Text fontSize="1em" color="white" as="span">
        {getFullDisplayBalance(lockAmount, 0, 5)} {selectedToken.name}
      </Text>
    </RowBetween>
  )
}

export enum EmptyMessageEnum {
  Currency,
  Network,
  Bank,
}
export const EmptyCurrencyResponse: React.FC<{
  message: EmptyMessageEnum
}> = ({ message }) => {
  return (
    <Column alignItems="center" minHeight="100px" py="20px">
      <TransText textAlign="center" small color="var(--pm-text-note-color)" fontWeight={500}>
        {message === EmptyMessageEnum.Currency
          ? 'No Currency match your search'
          : message === EmptyMessageEnum.Network
          ? 'No Network match your search'
          : 'No Bank match your search'}
      </TransText>
    </Column>
  )
}

export const PaymentQrCode: React.FC<{
  isLoading: boolean
  value: string
  selectedToken: Token
}> = ({ isLoading, selectedToken, value }) => {
  return (
    <Box p="4px" background="white" width="120px" height="120px" mx="auto">
      {isLoading ? (
        <Skeleton width={112} height={112} />
      ) : value ? (
        <QRCode
          imageSettings={{
            src: selectedToken.logo,
            width: 32,
            height: 32,
            excavate: false,
          }}
          size={112}
          value={value}
        />
      ) : (
        <Box width={112} height={112} />
      )}
    </Box>
  )
}

export const PaymentStroke = styled(Box)`
  height: 1px;
  width: 100%;
  background: var(--pm-stroke);
`

export const PaymentWrapIcon = styled(RowMiddle).attrs((props) => ({
  color: props.color || `var(--pm-text-note-color, ${props.theme.colors.textAlt5})`,
}))`
  cursor: pointer;
  transition: color 0.3s;
  color: ${({ color }) => color};

  svg {
    color: currentColor;
    fill: currentColor !important;

    path {
      fill: currentColor !important;
    }
  }

  &:hover {
    color: var(--pm-text-note-hover-color, ${({ theme }) => theme.colors.textTertiary});
  }
`

export const TextSelect: React.FC<
  React.PropsWithChildren<
    TextProps & {
      placeholder?: boolean
    }
  >
> = ({ placeholder, ...props }) => {
  return (
    <Text
      data-slot="TextSelect"
      small
      bold
      textAlign="left"
      color={placeholder ? 'var(--pm-text-note-color)' : 'var(--pm-color)'}
      {...props}
    />
  )
}
TextSelect.displayName = 'TextSelect'

export const PaymentWrapOption = styled(RowMiddle)<{ selected?: boolean; disabled?: boolean }>`
  cursor: pointer;
  border-width: 1px;
  border-style: solid;
  border-color: transparent;
  border-radius: var(--pm-trigger-radius);

  ${({ selected, theme }) =>
    selected &&
    css`
      background: var(--pm-card-active-hover) !important;
      border-color: ${theme.colors.primary};
    `}

  ${({ disabled }) =>
    disabled
      ? css`
          cursor: not-allowed;
          color: var(--pm-text-disabled);
          background: var(--pm-card-disabled);
        `
      : css`
          &:hover {
            background: var(--pm-card-active);
          }
        `};

  ${({ theme }) => theme.mediaQueries.md} {
    border-radius: var(--pm-trigger-radius);
  }
`
PaymentWrapOption.defaultProps = {
  padding: '8px',
  gap: '12px',
}

export const PaymentChevronRightIcon = styled(Icons.ChevronRightIcon)<{ rotate?: number }>`
  fill: none;

  transform: scale(1.3) rotate(var(--hp-rotate, unset));

  --hp-rotate: ${({ rotate }) => (rotate ? `${rotate}deg` : 'unset')};

  path {
    fill: currentColor;
  }
`
PaymentChevronRightIcon.displayName = 'PaymentChevronRightIcon'
PaymentChevronRightIcon.defaultProps = {
  color: 'var(--pm-label-color)',
  width: '7.79px',
  height: '11.69px',
}

export const CountListCurrencyDeposit = () => {
  const { display, totalRemain } = useListTokenActivePayment()

  return (
    <RowCenter>
      {display.slice(0, 5).map((currency, index) => (
        <Image
          mr={index < display.length - 1 ? '-6px' : 0}
          style={{
            zIndex: index,
          }}
          key={`fiat-source-${currency.code}`}
          src={currency.logo}
          width={20}
          height={20}
          alt={`fiat-${currency.code}`}
        />
      ))}
      <RowCenter
        width="24px"
        height="24px"
        borderRadius="50%"
        border={`2px solid ${theme.colors.textAlt7}66`}
        zIndex={5}
        background={theme.colors.background}
      >
        <Text fontSize="9px" letterSpacing="-0.2px" bold>
          +{totalRemain}
        </Text>
      </RowCenter>
    </RowCenter>
  )
}
